/* seedErrors.js — Operational outcome evaluator for /api/workspace/seed-sample-data.
 * Why this exists:
 *   The first 5 minutes of a new agency's life decide whether they stay.
 *   A generic "Network Error" or "Couldn't reach the seed service" silently
 *   kills activation. Every failure path must resolve to a calm, specific
 *   sentence the operator can act on — never an axios message, never a
 *   stack trace, never an exclamation mark.
 *
 * Hard guard doctrine (post 2026-05-19 hotfix):
 *   The seed pipeline is idempotent. The TRUTH about whether a seed
 *   succeeded lives in `/api/workspace/seed-status`, not in the axios
 *   result. Therefore:
 *
 *     1. If the backend returned an explicit `success:true` body         → SUCCESS
 *     2. Else if the post-call seed-status shows the workspace is
 *        populated (deals + contacts + properties all > 0)                → SUCCESS
 *     3. Else if the workspace counts grew vs. the pre-call snapshot     → SUCCESS
 *        (idempotent top-up landed even though axios saw a torn stream)
 *     4. Else if the backend returned an explicit `success:false` body   → operational error
 *     5. Else if axios surfaced a true transport failure (no truth and
 *        no populated workspace)                                          → calm transport copy
 *     6. Else                                                              → calm fallback
 *
 * Inputs we recognise:
 *   1. Structured success body:
 *        { success:true,  message, contacts_created, properties_created,
 *          deals_created, deals_existing, repaired, after, … }
 *   2. Structured failure body (200 with success:false):
 *        { success:false, error_code, message }
 *   3. Axios "Network Error" (err.message === "Network Error", no response)
 *   4. HTTP 4xx / 5xx with FastAPI detail shape:
 *        { detail: "string" } | { detail: { message, code } } |
 *        { detail: [ { msg, loc } ] }
 *
 * Outputs:
 *   { tone: "success" | "info" | "error", title, description? }
 */

const _isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

// Build marker — surfaced in every `[seed-trace]` line so the
// operator can confirm at a glance which evaluator version the
// browser actually ran. If you see `seed-evaluator: v152` in the
// trace, the bundle is one revision behind — bump it.
import { SEED_EVALUATOR_VERSION, BUNDLE_BUILT_AT } from "./buildVersion";

const _humanCounts = (after) => {
  if (!_isObj(after)) return null;
  const c = Number.isFinite(after.contacts)   ? after.contacts   : null;
  const p = Number.isFinite(after.properties) ? after.properties : null;
  const d = Number.isFinite(after.deals)      ? after.deals      : null;
  if (c === null && p === null && d === null) return null;
  const bits = [];
  if (c !== null) bits.push(`${c} contact${c === 1 ? "" : "s"}`);
  if (p !== null) bits.push(`${p} propert${p === 1 ? "y" : "ies"}`);
  if (d !== null) bits.push(`${d} active deal${d === 1 ? "" : "s"}`);
  return bits.join(" · ");
};

// A seed-status snapshot is "populated" when the backend reports
// `is_healthy: true` — the canonical operational truth. The flag
// requires contacts > 0 AND properties > 0 AND deals > 0 AND no
// orphan sample deals. We additionally back-fill from raw counts
// when the flag is absent (older backend builds during rolling
// deploy) so the guard remains robust mid-deploy.
const _statusPopulated = (s) => {
  if (!_isObj(s)) return false;
  if (s.is_healthy === true) return true;
  // Defensive back-fill for older responses lacking the flag.
  if (s.is_healthy === false) return false;
  return (
    s.is_empty !== true
    && (Number(s.deals)      || 0) > 0
    && (Number(s.contacts)   || 0) > 0
    && (Number(s.properties) || 0) > 0
  );
};

// A snapshot is "topped up" when AFTER counts strictly grew vs BEFORE.
// Used as a softer second-tier guard for the idempotent top-up case:
// when an admin already had a partial workspace and the seed call
// completed enough work to add records, we treat it as a calm success
// even if the axios transport torn mid-flight.
const _statusGrew = (before, after) => {
  if (!_isObj(after)) return false;
  if (!_isObj(before)) return false;
  const grew = (k) => (Number(after[k]) || 0) > (Number(before[k]) || 0);
  return grew("contacts") || grew("properties") || grew("deals");
};

/* ─── 1. Success/failure body mapper (kept for legacy callers) ──────── */

export function presentSeedResult(body) {
  if (!_isObj(body)) {
    return { tone: "error", title: "Couldn't read the workspace response", description: "Please try again." };
  }

  // Structured failure body (HTTP 200, success:false)
  if (body.success === false || body.ok === false) {
    const code = (body.error_code || "").toUpperCase();
    if (code === "SEED_TIMEOUT") {
      return {
        tone: "error",
        title: "Workspace setup took too long",
        description: "Your existing data remains safe. Try again — ChainFlow will pick up where it left off.",
      };
    }
    if (code === "SEED_INVALID_STATE") {
      // Backend ran two pipeline passes and verification still failed.
      // This is rare — surface the calm fallback copy + carry the
      // structured `errors` list for debug forensics.
      return {
        tone: "error",
        title: "Sample workspace finished in an inconsistent state",
        description: "Your existing data remains safe. Tap again — ChainFlow will reconcile what's missing.",
      };
    }
    if (code === "SEED_SERVICE_UNAVAILABLE") {
      return {
        tone: "error",
        title: body.message || "Couldn't set up your sample workspace",
        description: "Your existing data remains safe. Try again in a moment.",
      };
    }
    return {
      tone: "error",
      title: body.message || "Couldn't set up your sample workspace",
      description: "Your existing data remains safe.",
    };
  }

  // Structured success body
  const counts = _humanCounts(body.after);
  if (body.repaired) {
    // Self-healed a drifted workspace — give it a dedicated, calm
    // headline. The live counts go in the description so the
    // operator can confirm workspace health at a glance.
    return {
      tone: "success",
      title: "Recovered missing records",
      description: counts || body.message || "Sample workspace is back to a complete state.",
    };
  }
  const headline = body.message || body.summary || "Workspace already populated";
  return {
    tone: "success",
    title: headline,
    description: counts || undefined,
  };
}

/* ─── 2. Pure-error mapper (only called when the hard guard has
 *      already ruled out the populated/grew/success cases). The copy
 *      avoids any "infrastructure failure" wording — the workspace
 *      truth check has already verified the seed didn't land. ─────── */

export function presentSeedError(err) {
  // 1. True transport failure — no HTTP response received
  if (err && !err.response) {
    const msg = err?.message || "";
    const code = err?.code || "";
    if (code === "ERR_CANCELED" || /aborted/i.test(msg)) {
      // Request was cancelled (navigation, tab close, manual abort).
      // Operator does not need a toast — they intentionally walked
      // away. Caller may choose to suppress.
      return {
        tone: "info",
        title: "Sample data setup was interrupted",
        description: "No changes were lost. Tap Top up again when you're ready.",
      };
    }
    if (code === "ECONNABORTED" || /timeout/i.test(msg)) {
      return {
        tone: "error",
        title: "Sample data setup took too long to confirm",
        description: "Your existing data remains safe — tap again and ChainFlow will pick up where it left off.",
      };
    }
    // Genuine "Network Error" with no populated workspace afterward.
    // Calm operational wording — no "infrastructure failure", no
    // "Couldn't reach the service". Idempotent retry is safe.
    return {
      tone: "error",
      title: "Sample data didn't save — please tap again",
      description: "Your existing data remains safe. ChainFlow's seed pipeline is idempotent — a second tap will reconcile any missing records.",
    };
  }

  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  // 2. Structured backend detail
  if (_isObj(detail) && detail.message) {
    return { tone: "error", title: detail.message };
  }
  if (typeof detail === "string" && detail.trim()) {
    return { tone: "error", title: detail };
  }
  if (Array.isArray(detail) && detail[0]?.msg) {
    return { tone: "error", title: String(detail[0].msg).replace(/^Value error,\s*/i, "") };
  }

  // 3. HTTP-status fallback
  if (status === 401) return { tone: "error", title: "Please sign in to seed your workspace" };
  if (status === 403) return { tone: "error", title: "Only admins can seed sample data" };
  if (status === 429) return { tone: "info",  title: "Just a moment — try that again in a few seconds" };
  if (status && status >= 500) return {
    tone: "error",
    title: "Sample data couldn't be saved",
    description: "Your existing data is unaffected. Tap again in a moment — the seed pipeline is idempotent.",
  };
  return { tone: "error", title: "Couldn't set up your sample workspace", description: "Please try again in a moment." };
}

/* ─── 3. Hard-guard outcome evaluator — the ONLY decision surface
 *      callers should use. Centralises the success-first doctrine so
 *      that a populated workspace cannot ever be reported as a seed
 *      failure. ───────────────────────────────────────────────────── */

export function evaluateSeedOutcome({ body, err, after, before } = {}) {
  // [iter-153 LIVE TRACE] Maximum-forensic diagnostic. Captures
  // every input the evaluator decided on. Strip once the recurring
  // false-negative on the user's session is confirmed gone.
  const _trace = (rule, view) => {
    try {
      // eslint-disable-next-line no-console
      console.warn("[seed-trace]", {
        seed_evaluator: SEED_EVALUATOR_VERSION,
        bundle:         String(BUNDLE_BUILT_AT).slice(-12),
        rule,
        view_tone: view?.tone, view_title: view?.title,
        view_description: view?.description,
        body_success: body?.success, body_ok: body?.ok,
        body_code: body?.error_code, body_message: body?.message,
        body_keys: body && Object.keys(body),
        body_after_counts: body?.after && {
          c: body.after.contacts, p: body.after.properties, d: body.after.deals,
        },
        // Maximum-detail axios error capture — name, code, message,
        // request URL, response shape, headers presence. This is
        // what we need to disambiguate ERR_NETWORK vs CORS vs CSRF
        // vs timeout in a user's session we can't directly reach.
        err_present: !!err,
        err_name: err?.name,
        err_code: err?.code,
        err_message: err?.message,
        err_response_status: err?.response?.status,
        err_response_statusText: err?.response?.statusText,
        err_response_data: err?.response?.data,
        err_response_headers_present: err?.response?.headers ? Object.keys(err.response.headers).length : 0,
        err_request_url: err?.config?.url,
        err_request_method: err?.config?.method,
        err_request_withCredentials: err?.config?.withCredentials,
        err_request_timeout: err?.config?.timeout,
        before_present: !!before,
        before_healthy: before?.is_healthy,
        before_counts: before && {
          c: before.contacts, p: before.properties, d: before.deals,
        },
        after_present: !!after,
        after_healthy: after?.is_healthy,
        after_partial: after?.is_partial,
        after_empty: after?.is_empty,
        after_counts: after && {
          c: after.contacts, p: after.properties, d: after.deals,
        },
      });
    } catch { /* never throw from the trace */ }
    return view;
  };

  // RULE 1 — Explicit success body always wins. The backend already
  // verified integrity end-to-end before returning success:true.
  if (_isObj(body) && (body.success === true || body.ok === true)) {
    return _trace("rule_1_success_body", presentSeedResult(body));
  }

  // RULE 1b — Late-arriving structured success body. Some clients
  // (browser extensions, slow proxies, intermediaries with
  // response-body-rewriting) deliver `body.after.is_healthy === true`
  // without the explicit `success` flag. If the response body carries
  // a clearly-populated `after` counts dictionary, treat it as a
  // success. This closes the "success body served but flag missing"
  // edge case detected in iter-153 forensics.
  if (_isObj(body?.after)
      && (Number(body.after.contacts)   || 0) > 0
      && (Number(body.after.properties) || 0) > 0
      && (Number(body.after.deals)      || 0) > 0) {
    return _trace("rule_1b_body_after_populated", {
      tone: "success",
      title: "Workspace ready",
      description: _humanCounts(body.after) || "Sample data is in place.",
    });
  }

  // RULE 2 — Workspace is genuinely populated according to the
  // post-call seed-status truth probe. This is the canonical
  // false-negative suppression: axios may have thrown "Network Error"
  // because Cloudflare cut the stream after the backend had already
  // persisted everything. The truth is in the database, not the
  // request lifecycle.
  if (_statusPopulated(after)) {
    return _trace("rule_2_populated_after", {
      tone: "success",
      title: "Workspace ready",
      description: _humanCounts(after) || "Sample data is in place.",
    });
  }

  // RULE 3 — Counts strictly grew vs the pre-call snapshot. The
  // workspace isn't fully populated yet (maybe properties haven't
  // landed on this org), but the seed call demonstrably wrote new
  // records — that's a successful top-up. Show a calm progress
  // success rather than a scary error.
  if (_statusGrew(before, after)) {
    return _trace("rule_3_growth", {
      tone: "success",
      title: "Sample data topped up",
      description: _humanCounts(after) || "ChainFlow added the missing records.",
    });
  }

  // RULE 4 — Structured backend failure body (`success:false`). The
  // seed pipeline ran and the backend self-reported a known failure
  // mode — surface the calm code-mapped copy.
  if (_isObj(body) && (body.success === false || body.ok === false)) {
    return _trace("rule_4_structured_failure", presentSeedResult(body));
  }

  // RULE 5 — Genuine transport failure AND the truth check confirms
  // the workspace did not land. Only NOW do we surface a transport
  // error. Copy is operationally calm — never "Couldn't reach the
  // seed service" / "infrastructure failure" / generic axios wording.
  if (err) {
    return _trace("rule_5_transport_error", presentSeedError(err));
  }

  // RULE 6 — No body, no err, no truth signal. Defensive fallback.
  return _trace("rule_6_fallback", {
    tone: "error",
    title: "Couldn't confirm your sample data finished saving",
    description: "Your existing data remains safe. Please tap Top up again.",
  });
}
